// Phase 5 (api-phase5.md "Customer notification", D11): Setup → site → Notifications.
//   sending     manual (operators and the AI agent use Notify) or automatic (the SOC sends a template at set moments:
//               police dispatched, closed as activity, closed as all clear), per site. Admins change it; sites start manual.
//   recipients  who at the site gets texts and emails: a contact of the site or of its company in the ERP data, then one of their
//               SMS-capable phones or an email address, and what for (event notices, customer reports). The number or
//               address is read from the ERP contact when sending and never copied into the SOC. Admins add and remove.
//   templates   the texts and emails (all sites): activity / police / all_clear (SMS and email), report (email). Variables
//               {site} {time} {summary} {event} {report_url}. Admins edit; supervisors read.

import { useState } from 'react';
import { api, type Contact, type NotificationRecipient, type NotificationSettings, type NotificationTemplate, type NotifyChannel, type NotifyMoment, type NotifyPurpose } from '../../api.ts';
import { ContactOptions, isUsable } from '../../components/ContactOptions.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ErrorBox, InlineError, Loading, Pill } from '../../components/ui.tsx';
import { fmtPhone, human } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';
import { SaveBar, Toggle } from './common.tsx';

const PURPOSES: { value: NotifyPurpose; label: string; sub: string }[] = [
  { value: 'event_notice', label: 'Event notices', sub: 'activity, police dispatched, all clear' },
  { value: 'customer_report', label: 'Customer reports', sub: 'the incident report after an event closes (email)' },
];

const MOMENTS: { value: NotifyMoment; label: string; sub: string }[] = [
  { value: 'police_dispatched', label: 'Police dispatched', sub: 'an operator records a police dispatch on the event' },
  { value: 'closed_genuine', label: 'Closed as activity', sub: 'disposition: non-criminal, criminal or life safety' },
  { value: 'closed_clear', label: 'Closed as all clear', sub: 'disposition: false alarm or authorized' },
];

/** Manual or automatic event notices for the site, and which template each moment sends. */
function Sending({ siteId, canEdit }: { siteId: string; canEdit: boolean }) {
  const load = useLoad((sig) => api.notificationSettings(siteId, sig), [siteId]);
  const tpl = useLoad((sig) => api.notificationTemplates(sig), []);
  const [d, setD] = useState<NotificationSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const cur = d ?? load.data;
  const codes = [...new Set((tpl.data ?? []).filter((t) => t.purpose === 'event_notice' && t.active).map((t) => t.code))];
  const nameOf = (code: string) => (tpl.data ?? []).find((t) => t.code === code)?.name ?? code;
  const edit = (f: (s: NotificationSettings) => NotificationSettings) => {
    if (!cur) return;
    setD(f(cur));
    setSaved(false);
  };
  const save = async () => {
    if (!d) return;
    setBusy(true);
    setErr(null);
    try {
      load.setData(await api.putNotificationSettings(siteId, { mode: d.mode, moments: d.moments }));
      setD(null);
      setSaved(true);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card2">
      <div className="card-h">
        <h3>
          <Icon name="send" />
          Sending
        </h3>
        <span className="card-r note">For this site only</span>
      </div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the notification setting" />
      ) : !cur ? (
        <Loading label="Loading…" />
      ) : (
        <fieldset disabled={!canEdit || busy} className="tdform">
          <div className="seg" role="radiogroup" aria-label="Event notices">
            {(['manual', 'automatic'] as const).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={cur.mode === m} className={cur.mode === m ? 'on' : ''} onClick={() => edit((s) => ({ ...s, mode: m }))}>
                {m === 'manual' ? 'Manual' : 'Automatic'}
              </button>
            ))}
          </div>
          <div className="note">
            {cur.mode === 'manual'
              ? 'Nothing is sent unless an operator uses Notify on the event, or the AI agent sends a notice (Level 3+).'
              : "The SOC sends these to the site's event-notice recipients, by text and email, once per event at each moment. Operators can still use Notify; a notice already sent for the event isn't sent again."}
          </div>
          {cur.mode === 'automatic' &&
            MOMENTS.map((m) => (
              <label key={m.value} className="field">
                <span className="lbl">
                  {m.label} <span className="dim">· {m.sub}</span>
                </span>
                <select value={cur.moments[m.value] ?? ''} onChange={(e) => edit((s) => ({ ...s, moments: { ...s.moments, [m.value]: e.target.value || null } }))}>
                  <option value="">Don't send</option>
                  {[...new Set([...codes, ...(cur.moments[m.value] ? [cur.moments[m.value]!] : [])])].map((c) => (
                    <option key={c} value={c}>
                      {nameOf(c)} ({c}){c === cur.defaults[m.value] ? ' · default' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          {cur.mode === 'automatic' && (
            <div className="note">Events at a site that isn't live yet, and events closed as technical, other or merged, send nothing. A notice with no recipients is recorded on the event as not sent.</div>
          )}
        </fieldset>
      )}
      {cur && <SaveBar canEdit={canEdit} dirty={!!d} busy={busy} error={err} saved={saved} onSave={() => void save()} onReset={() => setD(null)} />}
    </section>
  );
}

function contactLine(c: Contact | undefined, r: NotificationRecipient): string {
  if (r.channel === 'sms') {
    const p = c?.phones.find((x) => x.phone_id === r.phoneId);
    return p ? `${p.type} · ${fmtPhone(p.e164, p.extension)}` : `phone ${r.phoneId ?? '?'} (not in the ERP data any more)`;
  }
  const m = c?.emails.find((x) => x.email_id === r.emailId);
  return m ? m.address : `email ${r.emailId ?? '?'} (not in the ERP data any more)`;
}

function Recipients({ siteId, contacts, canEdit }: { siteId: string; contacts: Contact[]; canEdit: boolean }) {
  const load = useLoad((sig) => api.notificationRecipients(siteId, sig), [siteId]);
  const byId = new Map(contacts.map((c) => [c.id, c]));
  const active = contacts.filter(isUsable);
  const [contactId, setContactId] = useState('');
  const [channel, setChannel] = useState<NotifyChannel>('sms');
  const [target, setTarget] = useState('');
  const [purposes, setPurposes] = useState<NotifyPurpose[]>(['event_notice']);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const c = byId.get(contactId);
  const phones = (c?.phones ?? []).filter((p) => p.sms_capable);
  const emails = c?.emails ?? [];
  const add = async () => {
    if (!contactId || !target || !purposes.length) return;
    setBusy('add');
    setErr(null);
    try {
      const r = await api.addNotificationRecipient(siteId, { contactId, channel, ...(channel === 'sms' ? { phoneId: target } : { emailId: target }), purposes });
      load.setData((l) => [...(l ?? []), r]);
      setContactId('');
      setTarget('');
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  const remove = async (r: NotificationRecipient) => {
    setBusy(r.id);
    setErr(null);
    try {
      await api.removeNotificationRecipient(siteId, r.id);
      load.setData((l) => l?.filter((x) => x.id !== r.id));
      setConfirm(null);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  const pickContact = (id: string, ch = channel) => {
    setContactId(id);
    const cc = byId.get(id);
    setTarget(ch === 'sms' ? (cc?.phones.find((p) => p.sms_capable)?.phone_id ?? '') : (cc?.emails.find((m) => m.is_primary)?.email_id ?? cc?.emails[0]?.email_id ?? ''));
  };
  return (
    <section className="card2">
      <div className="card-h">
        <h3>
          <Icon name="users" />
          Recipients
        </h3>
        <span className="card-r note">Names, numbers and addresses come from the ERP contact when a message is sent.</span>
      </div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the recipients" />
      ) : !load.data ? (
        <Loading label="Loading recipients…" />
      ) : load.data.length === 0 ? (
        <div className="note warn-t">Nobody is set up. Event notices can't be sent, and customer reports stay with the event.</div>
      ) : (
        <div className="nrecips">
          {load.data.map((r) => {
            const cc = byId.get(r.contactId);
            return (
              <div key={r.id} className={`editrow nrecip${r.active ? '' : ' off'}`}>
                <Icon name={r.channel === 'email' ? 'mail' : 'phone'} />
                <span>
                  <b>{r.contactName ?? cc?.displayName ?? r.contactId}</b>
                  <div className="note">
                    {r.channel === 'email' ? 'Email' : 'SMS'} · {contactLine(cc, r)}
                    {cc && !isUsable(cc) ? ' · contact inactive in the ERP' : ''}
                  </div>
                </span>
                <span className="chips">
                  {r.purposes.map((p) => (
                    <span key={p} className="chip">
                      {PURPOSES.find((x) => x.value === p)?.label ?? human(p)}
                    </span>
                  ))}
                </span>
                {canEdit &&
                  (confirm === r.id ? (
                    <span className="row-wrap nowrap">
                      <button type="button" className="btn sm danger" onClick={() => void remove(r)} disabled={busy === r.id}>
                        {busy === r.id ? 'Removing…' : 'Remove'}
                      </button>
                      <button type="button" className="btn sm" onClick={() => setConfirm(null)}>
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button type="button" className="btn icon sm" onClick={() => setConfirm(r.id)} aria-label={`Remove ${r.contactName ?? 'recipient'}`} title="Remove">
                      <Icon name="trash" />
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      {canEdit && (
        <div className="sec">
          <div className="lbl">Add a recipient</div>
          {active.length === 0 ? (
            <div className="note">No ERP contacts for this site or its company.</div>
          ) : (
            <>
              <div className="fgrid">
                <label className="field">
                  <span className="lbl">Contact (site or company)</span>
                  <select value={contactId} onChange={(e) => pickContact(e.target.value)}>
                    <option value="">Choose a contact…</option>
                    <ContactOptions contacts={active} />
                  </select>
                </label>
                <label className="field">
                  <span className="lbl">By</span>
                  <select
                    value={channel}
                    onChange={(e) => {
                      const ch = e.target.value === 'email' ? 'email' : 'sms';
                      setChannel(ch);
                      if (ch === 'sms') setPurposes((p) => p.filter((x) => x !== 'customer_report').length ? p.filter((x) => x !== 'customer_report') : ['event_notice']);
                      pickContact(contactId, ch);
                    }}
                  >
                    <option value="sms">Text message (SMS)</option>
                    <option value="email">Email</option>
                  </select>
                </label>
                <label className="field">
                  <span className="lbl">{channel === 'sms' ? 'SMS-capable phone' : 'Email address'}</span>
                  <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={!contactId}>
                    <option value="">{!contactId ? 'Choose a contact first' : channel === 'sms' ? (phones.length ? 'Choose a phone…' : 'No SMS-capable phone') : emails.length ? 'Choose an address…' : 'No email address'}</option>
                    {channel === 'sms'
                      ? phones.map((p) => (
                          <option key={p.phone_id} value={p.phone_id}>
                            {p.type} · {fmtPhone(p.e164, p.extension)}
                          </option>
                        ))
                      : emails.map((m) => (
                          <option key={m.email_id} value={m.email_id}>
                            {m.address}
                          </option>
                        ))}
                  </select>
                  {contactId && channel === 'sms' && (c?.phones.length ?? 0) > phones.length && <span className="note">Phones that can't take texts are left out (ERP: sms_capable).</span>}
                </label>
              </div>
              <div className="row-wrap" role="group" aria-label="Purposes">
                {PURPOSES.map((p) => (
                  <Toggle
                    key={p.value}
                    checked={purposes.includes(p.value)}
                    onChange={(v) => setPurposes((l) => (v ? [...l, p.value] : l.filter((x) => x !== p.value)))}
                    label={`${p.label} (${p.sub})`}
                    disabled={p.value === 'customer_report' && channel === 'sms'}
                  />
                ))}
              </div>
              {channel === 'sms' && <div className="note">Customer reports go by email only.</div>}
              <div className="savebar">
                <button type="button" className="btn pri" onClick={() => void add()} disabled={!contactId || !target || !purposes.length || busy === 'add'}>
                  <Icon name="plus" />
                  {busy === 'add' ? 'Adding…' : 'Add recipient'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <InlineError error={err} />
    </section>
  );
}

function TemplateRow({ t, canEdit, onSaved }: { t: NotificationTemplate; canEdit: boolean; onSaved: () => void }) {
  const [d, setD] = useState<NotificationTemplate | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const save = async () => {
    if (!d) return;
    setBusy(true);
    setErr(null);
    try {
      await api.putNotificationTemplate(t.code, t.channel, { purpose: d.purpose, name: d.name.trim(), subject: d.channel === 'email' ? d.subject?.trim() || null : null, body: d.body.trim(), language: d.language, agentAllowed: d.agentAllowed, active: d.active });
      setD(null);
      onSaved();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`tdmsg${t.active ? '' : ' off'}`}>
      <div className="row-wrap">
        <Icon name={t.channel === 'email' ? 'mail' : 'phone'} />
        <b>{t.name}</b>
        <span className="mono dim">
          {t.code} · {t.channel} · {t.language}
        </span>
        <span className="pill tiny-pill">{t.purpose === 'customer_report' ? 'customer report' : 'event notice'}</span>
        {t.agentAllowed && t.purpose === 'event_notice' && <Pill tone="ai">AI may send (Level 3+)</Pill>}
        {!t.active && <Pill tone="warn">inactive</Pill>}
        <span className="sp" />
        {canEdit && !d && (
          <button type="button" className="btn sm" onClick={() => setD({ ...t })}>
            Edit
          </button>
        )}
      </div>
      {!d ? (
        <>
          {t.subject && <div className="small strong">{t.subject}</div>}
          <div className="small pre">{t.body}</div>
        </>
      ) : (
        <fieldset disabled={busy} className="tdform">
          <label className="field">
            <span className="lbl">Name</span>
            <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
          </label>
          {d.channel === 'email' && (
            <label className="field">
              <span className="lbl">Subject</span>
              <input value={d.subject ?? ''} onChange={(e) => setD({ ...d, subject: e.target.value })} />
            </label>
          )}
          <label className="field">
            <span className="lbl">Text · {'{site} {time} {summary} {event} {report_url}'}{d.channel === 'sms' ? ' · up to 480 characters once filled' : ''}</span>
            <textarea rows={d.channel === 'email' ? 7 : 3} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} />
          </label>
          <div className="row-wrap">
            <Toggle checked={d.agentAllowed} onChange={(v) => setD({ ...d, agentAllowed: v })} label="The AI agent may send it" disabled={busy} />
            <Toggle checked={d.active} onChange={(v) => setD({ ...d, active: v })} label="Active" disabled={busy} />
          </div>
          <div className="savebar">
            <button type="button" className="btn pri" onClick={() => void save()} disabled={busy || !d.name.trim() || !d.body.trim()}>
              <Icon name="check" />
              {busy ? 'Saving…' : 'Save template'}
            </button>
            <button type="button" className="btn" onClick={() => setD(null)}>
              Cancel
            </button>
            <span className="note">Applies to every site.</span>
          </div>
        </fieldset>
      )}
      <InlineError error={err} />
    </div>
  );
}

function Templates({ canEdit }: { canEdit: boolean }) {
  const load = useLoad((sig) => api.notificationTemplates(sig), []);
  return (
    <section className="card2">
      <div className="card-h">
        <h3>
          <Icon name="mail" />
          Templates
        </h3>
        <span className="card-r note">Shared by all sites{canEdit ? '' : ' · admins edit them'}</span>
      </div>
      {load.error && !load.data ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the templates" />
      ) : !load.data ? (
        <Loading label="Loading templates…" />
      ) : load.data.length === 0 ? (
        <div className="note">No templates.</div>
      ) : (
        load.data.map((t) => <TemplateRow key={`${t.code}-${t.channel}-${t.language}`} t={t} canEdit={canEdit} onSaved={load.reload} />)
      )}
    </section>
  );
}

export function NotificationsTab({ siteId, contacts, canEdit }: { siteId: string; contacts: Contact[]; canEdit: boolean }) {
  return (
    <div className="tabbody">
      <div className="banner">
        <Icon name="send" />
        <span>
          Texts go through RingCentral and emails through Microsoft Graph (or an SMTP relay). Until those are configured on the server, messages are recorded as “simulated” and nothing is sent.
        </span>
      </div>
      <Sending siteId={siteId} canEdit={canEdit} />
      <Recipients siteId={siteId} contacts={contacts} canEdit={canEdit} />
      <Templates canEdit={canEdit} />
    </div>
  );
}
